import h from 'hyperscript';

import { createResourceLabel } from 'ui/resource-label/resource-label';
import type { HeadlineOptions } from 'ui/resource-summary/resource-summary';
import { createResourceSummary } from 'ui/resource-summary/resource-summary';
import { type DataSourceLabelOptions, createDataSourceLabel } from 'ui/resource-summary/resource-summary-utils';

import { createPublicationLabel } from 'cadenza/admin/publish-objecttype-dialog/publish-objecttype-actions';
import type { ObjecttypeDto } from 'cadenza/api-client/dto/objecttype-dto';
import { DataSourceId } from 'cadenza/repository/model/data-source-id.gen';
import { getTableResourceType } from 'cadenza/repository/model/data-source-type-util';
import { isBoundToWorkbookOrTransient } from 'cadenza/repository/model/objecttype-bound-state.gen';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import 'cadenza/ui/help-tooltip/help-tooltip';
import {
  createObjecttypeDetailsUrl,
  dataSourceAnchorWithResourceLabel,
  projectAnchorWithResourceLabel,
  repositoryAnchorWithResourceLabel,
} from 'cadenza/utils/admin-url-utils';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';

import i18n from './objecttype-summary.properties';

interface ObjecttypeMetaLabelOptions extends DataSourceLabelOptions {
  repositoryPrintName: string;
  tableName?: string;
}

type ObjecttypeSummaryObjecttype = Pick<
  Objecttype | ObjecttypeDto,
  | 'id'
  | 'printName'
  | 'isCombinedObjecttype'
  | 'isCdsObjecttype'
  | 'boundState'
  | 'dataSourceName'
  | 'projectId'
  | 'projectPrintName'
  | 'projectKind'
  | 'publicationState'
> &
  ObjecttypeMetaLabelOptions & {
    withManagementLink?: boolean;
    withManageObjecttypeAction?: boolean;
    withPublicationState?: boolean;
    withObjecttypeDescription?: boolean;
    repositoryName?: RepositoryName;
  };

export function createObjecttypeSummary(objecttype: ObjecttypeSummaryObjecttype, canSeeDatasource?: boolean) {
  const metaLabels: HTMLElement[] = [];
  if (objecttype.withObjecttypeDescription) {
    if (objecttype.isCombinedObjecttype) {
      metaLabels.push(h('.d-stack-h.space-0_5', i18n('objecttype.combined')));
    } else if (objecttype.isCdsObjecttype) {
      metaLabels.push(
        h('.d-stack-h.space-0_5', [
          i18n('objecttype.adaptive'),
          h(`d-help-tooltip.float-right`, { 'data-topic-id': 'objecttype.adaptive' }),
        ]),
      );
    }
  }
  // Initially, we only have the repository print name, so we cannot link the resource label to the repository
  const repositoryLabel = createRepositoryLabel(objecttype);
  metaLabels.push(repositoryLabel);
  if (objecttype.projectId) {
    metaLabels.push(
      projectAnchorWithResourceLabel({
        itemId: objecttype.projectId,
        projectKind: objecttype.projectKind!,
        printName: objecttype.projectPrintName!,
      }),
    );
  } else if (!objecttype.isCdsObjecttype) {
    metaLabels.push(
      canSeeDatasource && objecttype.repositoryName ?
        dataSourceAnchorWithResourceLabel(
          DataSourceId.of(objecttype.repositoryName, objecttype.dataSourceName),
          objecttype.dataSourcePrintName,
        )
      : createDataSourceLabel(objecttype),
    );
  }

  if (objecttype.tableName && !objecttype.isCdsObjecttype) {
    metaLabels.push(createResourceLabel(getTableResourceType(objecttype.dataSourceType), objecttype.tableName));
  }

  if (objecttype.withPublicationState) {
    metaLabels.push(createPublicationLabel(objecttype.publicationState));
  }

  const header = createResourceLabel(
    objecttype.isCombinedObjecttype ? 'combinedObjecttype' : 'objecttype',
    objecttype.printName,
  );
  const headlineOptions: HeadlineOptions = {};

  if (objecttype.withManagementLink) {
    headlineOptions.managementCenterLink = {
      url: createObjecttypeDetailsUrl(objecttype.id),
      label: i18n('openInManagementCenter.objecttype'),
    };
  } else if (objecttype.withManageObjecttypeAction) {
    headlineOptions.manageObjecttypeAction = {
      execute: async () => {
        const { ManageObjecttypeDialog } =
          await import('cadenza/objecttype/manage-objecttype-dialog/manage-objecttype-dialog');
        new ManageObjecttypeDialog(objecttype).connectAndShow();
      },
      label: i18n('openManageObjecttype'),
    };
  }
  return createResourceSummary(header, metaLabels, headlineOptions);
}

function createRepositoryLabel(objecttype: ObjecttypeSummaryObjecttype) {
  if (isBoundToWorkbookOrTransient(objecttype.boundState)) {
    return createResourceLabel('workbook', i18n('workbook.storage'));
  }
  return objecttype.repositoryName ?
      repositoryAnchorWithResourceLabel(objecttype.repositoryName, objecttype.repositoryPrintName)
    : createResourceLabel('repository', objecttype.repositoryPrintName);
}
